import React, { useState, useEffect, useMemo } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { setAdminData, addStore, updateStore, removeStore } from '../store/adminSlice';
import { PW_Stores_API, PW_Store_Save_API, PW_Store_Delete_API } from '../Server/ApiV2';
import Spinner from './Spinner';

export default function Stores() {
  const dispatch = useDispatch();
  const { stores } = useSelector(s => s.admin);
  const [pageLoading, setPageLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      const res = await PW_Stores_API();
      if (cancelled) return;
      if (res?.data?.success) dispatch(setAdminData({ stores: res.data.data }));
      setPageLoading(false);
    })();
    return () => { cancelled = true; };
  }, [dispatch]);
  const [showAdd, setShowAdd] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState({ name: '', code: '', location: '', city: '', status: 'Active' });

  const resetForm = () => setForm({ name: '', code: '', location: '', city: '', status: 'Active' });

  const refreshStores = async () => {
    const res = await PW_Stores_API();
    if (res?.data?.success) dispatch(setAdminData({ stores: res.data.data }));
  };

  const handleAdd = async () => {
    if (!form.name.trim() || !form.code.trim()) return;
    const id = `STR-${Date.now()}`;
    const storeData = { ...form, id, createdAt: new Date().toISOString().split('T')[0] };
    try {
      await PW_Store_Save_API(storeData);
      await refreshStores();
    } catch (err) { console.error('[Stores] Save failed:', err); }
    resetForm();
    setShowAdd(false);
  };

  const handleEdit = (store) => {
    setEditingId(store.id);
    setForm({ name: store.name, code: store.code, location: store.location || '', city: store.city || '', status: store.status });
  };

  const handleUpdate = async () => {
    const updateData = { id: editingId, ...form };
    try {
      await PW_Store_Save_API(updateData);
      await refreshStores();
    } catch (err) { console.error('[Stores] Update failed:', err); }
    setEditingId(null);
    resetForm();
  };

  const [deleting, setDeleting] = useState(null);

  const handleDelete = async (store) => {
    if (!window.confirm(`Delete store "${store.name}"? This cannot be undone.`)) return;
    setDeleting(store.id);
    try {
      await PW_Store_Delete_API(store.id);
      dispatch(removeStore(store.id));
      await refreshStores();
    } catch (err) { console.error('[Stores] Delete failed:', err); }
    setDeleting(null);
  };

  const [filter, setFilter] = useState('');
  const [sortCol, setSortCol] = useState('name');
  const [sortDir, setSortDir] = useState('asc');

  const handleSort = (col) => {
    if (sortCol === col) {
      setSortDir(d => d === 'asc' ? 'desc' : 'asc');
    } else {
      setSortCol(col);
      setSortDir('asc');
    }
  };

  const filtered = useMemo(() => {
    const q = filter.toLowerCase();
    let list = stores;
    if (q) {
      list = stores.filter(s =>
        s.id?.toLowerCase().includes(q) ||
        s.name?.toLowerCase().includes(q) ||
        s.code?.toLowerCase().includes(q) ||
        s.location?.toLowerCase().includes(q) ||
        s.city?.toLowerCase().includes(q) ||
        s.status?.toLowerCase().includes(q) ||
        s.createdAt?.toLowerCase().includes(q)
      );
    }
    return [...list].sort((a, b) => {
      const va = String(a[sortCol] || '').toLowerCase();
      const vb = String(b[sortCol] || '').toLowerCase();
      if (va < vb) return sortDir === 'asc' ? -1 : 1;
      if (va > vb) return sortDir === 'asc' ? 1 : -1;
      return 0;
    });
  }, [stores, filter, sortCol, sortDir]);

  const sortIcon = (col) => {
    if (sortCol !== col) return ' ↕';
    return sortDir === 'asc' ? ' ↑' : ' ↓';
  };

  if (pageLoading) return <div className="page-loading"><Spinner /></div>;

  return (
    <div>
      <div className="page-header">
        <div>
          <h2>Stores</h2>
          <div className="page-header-sub">Manage store locations</div>
        </div>
        <button className="btn btn-gold" onClick={() => { resetForm(); setShowAdd(true); setEditingId(null); }}>+ Add Store</button>
      </div>

      {(showAdd || editingId) && (
        <div className="card mb-24">
          <div className="card-title gold">{editingId ? 'Edit Store' : 'New Store'}</div>
          <div className="card-body">
            <div className="form-grid mb-16">
              <div className="form-group">
                <label className="form-label">Store Name</label>
                <input className="form-input" value={form.name} onChange={e => setForm({ ...form, name: e.target.value })} />
              </div>
              <div className="form-group">
                <label className="form-label">Store Code</label>
                <input className="form-input" value={form.code} onChange={e => setForm({ ...form, code: e.target.value })} />
              </div>
            </div>
            <div className="form-grid mb-16">
              <div className="form-group">
                <label className="form-label">Location</label>
                <input className="form-input" value={form.location} onChange={e => setForm({ ...form, location: e.target.value })} />
              </div>
              <div className="form-group">
                <label className="form-label">City</label>
                <input className="form-input" value={form.city} onChange={e => setForm({ ...form, city: e.target.value })} />
              </div>
            </div>
            {editingId && (
              <div className="form-group mb-16">
                <label className="form-label">Status</label>
                <select className="form-select" style={{ width: 200 }} value={form.status} onChange={e => setForm({ ...form, status: e.target.value })}>
                  <option value="Active">Active</option>
                  <option value="Inactive">Inactive</option>
                </select>
              </div>
            )}
            <div style={{ display: 'flex', gap: 8 }}>
              <button className="btn btn-success" onClick={editingId ? handleUpdate : handleAdd}
                disabled={!form.name.trim() || !form.code.trim()}>
                {editingId ? 'Update' : 'Create'} Store
              </button>
              <button className="btn btn-secondary" onClick={() => { setShowAdd(false); setEditingId(null); resetForm(); }}>Cancel</button>
            </div>
          </div>
        </div>
      )}

      <div className="card">
        <div className="card-title">
          Stores
          <span style={{ marginLeft: 'auto', fontSize: 12, color: '#6b6b80' }}>{filtered.length} of {stores.length} stores</span>
        </div>
        <div style={{ padding: '8px 16px' }}>
          <input
            className="form-input"
            placeholder="Search stores..."
            value={filter}
            onChange={e => setFilter(e.target.value)}
            style={{ maxWidth: 300 }}
          />
        </div>
        <div className="card-body" style={{ padding: 0 }}>
          <div className="table-wrap">
            <table className="data-table">
              <thead>
                <tr>
                  <th style={{ cursor: 'pointer' }} onClick={() => handleSort('id')}>ID{sortIcon('id')}</th>
                  <th style={{ cursor: 'pointer' }} onClick={() => handleSort('name')}>Name{sortIcon('name')}</th>
                  <th style={{ cursor: 'pointer' }} onClick={() => handleSort('code')}>Code{sortIcon('code')}</th>
                  <th style={{ cursor: 'pointer' }} onClick={() => handleSort('location')}>Location{sortIcon('location')}</th>
                  <th style={{ cursor: 'pointer' }} onClick={() => handleSort('city')}>City{sortIcon('city')}</th>
                  <th style={{ cursor: 'pointer' }} onClick={() => handleSort('status')}>Status{sortIcon('status')}</th>
                  <th style={{ cursor: 'pointer' }} onClick={() => handleSort('createdAt')}>Created{sortIcon('createdAt')}</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {filtered.map(s => (
                  <tr key={s.id}>
                    <td data-label="ID" className="text-sm text-muted">{s.id}</td>
                    <td data-label="Name" className="fw-600">{s.name}</td>
                    <td data-label="Code"><span className="chip chip-blue">{s.code}</span></td>
                    <td data-label="Location">{s.location}</td>
                    <td data-label="City">{s.city}</td>
                    <td data-label="Status"><span className={s.status === 'Active' ? 'status-active' : 'status-inactive'}>{s.status}</span></td>
                    <td data-label="Created" className="text-sm text-muted">{s.createdAt?.split('T')[0]}</td>
                    <td data-label="Actions" style={{ display: 'flex', gap: 4 }}>
                      <button className="btn btn-outline btn-sm" onClick={() => handleEdit(s)}>Edit</button>
                      <button className="btn btn-outline btn-sm" style={{ color: '#ef4444', borderColor: '#ef4444' }}
                        disabled={deleting === s.id}
                        onClick={() => handleDelete(s)}>
                        {deleting === s.id ? '...' : 'Delete'}
                      </button>
                    </td>
                  </tr>
                ))}
                {filtered.length === 0 && (
                  <tr><td colSpan={8} style={{ textAlign: 'center', padding: 24, color: '#6b6b80' }}>No stores found</td></tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  );
}
